"use no memo";

import { VatRateSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { VatRate } from "~/providers/general-provider";
import { useVatRateManager } from "~/managers/use-vat-rate-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";

export default function VatRateForm({
  vatRate,
  onClose,
}: {
  vatRate?: VatRate;
  onClose: (force?: boolean) => void;
}) {
  const { createVatRate, updateVatRate } = useVatRateManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const { t, i18n } = useTranslation("common");

  const form = useForm({
    schema: VatRateSchema(i18n),
    defaultValues: {
      name: vatRate?.name ?? "",
      value: vatRate?.value ?? 0,
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    if (vatRate) {
      const result = await updateVatRate(vatRate.id, data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const result = await createVatRate(data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        id="vat-rate-form"
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("VatRate.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("VatRate.value")}</FormLabel>
              <FormControl>
                <ModalInput type="number" min={0} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
